/**
 * BOUDANE WATCHES — SHOPIFY THEME JS v3.0
 * Maison Noire Design System
 *
 * BUG FIXES:
 * - cart-drawer aria-hidden now properly toggled
 * - body scroll lock uses class + overflow for both nav AND cart, properly restored
 * - mobile nav z-index (now 1050 in CSS, above cart-overlay 1100 only when both open prevented)
 * - qty-input readonly: JS writes value AND keeps hidden name="quantity" input in sync
 * - Add to cart error handling improved (shows description from Shopify)
 * - Cart drawer refresh after add (fetches real cart state)
 * - Newsletter: now actually prevents double-submit
 * - Hero parallax: only runs when hero is in viewport, uses requestAnimationFrame
 * - Active nav: exact match AND startsWith for collection/products routes
 * - Announcement bar: calculates offset for fixed header
 */

(function () {
  'use strict';

  // ============================================================
  // UTILITY
  // ============================================================
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function formatMoney(cents, currency) {
    const activeCurrency = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: activeCurrency }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${activeCurrency}`;
    }
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, ch => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[ch]));
  }

  // ============================================================
  // SHOPIFY FORMS POPUP — prevent autofocus from moving the page
  // ============================================================
  // Shopify Forms can focus the email field as its overlay opens. Because app
  // embeds are mounted near the end of the document, some browsers scroll the
  // storefront toward that mount point. Preserve the visitor's position unless
  // they intentionally scroll.
  (() => {
    let preservedY = window.scrollY;
    let userIsScrolling = false;
    let restoreTimer;

    const markIntentionalScroll = () => {
      userIsScrolling = true;
      clearTimeout(restoreTimer);
      restoreTimer = setTimeout(() => {
        preservedY = window.scrollY;
        userIsScrolling = false;
      }, 250);
    };

    ['wheel', 'touchmove', 'pointerdown'].forEach(type =>
      window.addEventListener(type, markIntentionalScroll, { passive: true })
    );
    window.addEventListener('keydown', event => {
      if (['PageDown', 'PageUp', 'ArrowDown', 'ArrowUp', 'Home', 'End', ' '].includes(event.key)) {
        markIntentionalScroll();
      }
    });

    const formsSelector = [
      'shopify-forms-embed',
      'shopify-forms-popup',
      '[id*="shopify-forms" i]',
      '[class*="shopify-forms" i]',
      'iframe[src*="forms" i]',
      'iframe[title*="form" i]'
    ].join(',');

    const restorePositionIfNeeded = () => {
      if (userIsScrolling) return;
      const formApp = document.querySelector(formsSelector);
      if (!formApp) return;
      if (Math.abs(window.scrollY - preservedY) < 40) return;

      // Use the legacy two-argument form to avoid smooth-scroll CSS or browser
      // behavior affecting the restoration.
      window.scrollTo(0, preservedY);
      requestAnimationFrame(() => window.scrollTo(0, preservedY));
    };

    const observer = new MutationObserver(() => {
      requestAnimationFrame(restorePositionIfNeeded);
      setTimeout(restorePositionIfNeeded, 80);
      setTimeout(restorePositionIfNeeded, 300);
    });

    const startObserver = () => {
      if (!document.body) return;
      preservedY = window.scrollY;
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden', 'open']
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startObserver, { once: true });
    } else {
      startObserver();
    }
  })();

  // Smooth-scroll only deliberate same-page anchor clicks. This preserves the
  // polished effect without applying smooth scrolling to app autofocus.
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href === '#') return;
    const target = document.querySelector(href);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', href);
  });

  // ============================================================
  // ANNOUNCEMENT BAR — push header below it
  // ============================================================
  const announceBar = $('.announcement-bar');
  const siteHeader  = $('.site-header');

  function setHeaderOffset() {
    if (announceBar && siteHeader) {
      const h = announceBar.offsetHeight;
      siteHeader.style.top = h + 'px';
    }
  }
  setHeaderOffset();
  window.addEventListener('resize', setHeaderOffset, { passive: true });

  // ============================================================
  // HEADER SCROLL BEHAVIOUR
  // ============================================================
  if (siteHeader) {
    const onScroll = () => {
      const threshold = announceBar ? announceBar.offsetHeight + 20 : 20;
      siteHeader.classList.toggle('scrolled', window.scrollY > threshold);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ============================================================
  // HERO PARALLAX + LOAD ANIMATION
  // ============================================================
  const hero    = $('.hero');
  const heroBg  = $('.hero-bg');
  const isMobile = () => window.innerWidth <= 960;

  if (hero) {
    requestAnimationFrame(() => hero.classList.add('loaded'));

    if (heroBg && !isMobile()) {
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        requestAnimationFrame(() => {
          const scrolled = window.scrollY;
          if (scrolled < window.innerHeight * 1.2) {
            heroBg.style.transform = `scale(1) translateY(${scrolled * 0.22}px)`;
          }
          ticking = false;
        });
        ticking = true;
      }, { passive: true });
    }
  }

  // ============================================================
  // MOBILE NAVIGATION
  // ============================================================
  const mobileMenuBtn  = $('.mobile-menu-btn');
  const mobileNav      = $('.mobile-nav');
  const mobileNavClose = $('.mobile-nav-close');

  function openMobileNav() {
    mobileNav  && mobileNav.classList.add('open');
    mobileMenuBtn && mobileMenuBtn.classList.add('active');
    document.body.classList.add('nav-open');
    mobileMenuBtn && mobileMenuBtn.setAttribute('aria-expanded', 'true');
    mobileNav  && mobileNav.setAttribute('aria-hidden', 'false');
  }

  function closeMobileNav() {
    mobileNav  && mobileNav.classList.remove('open');
    mobileMenuBtn && mobileMenuBtn.classList.remove('active');
    document.body.classList.remove('nav-open');
    mobileMenuBtn && mobileMenuBtn.setAttribute('aria-expanded', 'false');
    mobileNav  && mobileNav.setAttribute('aria-hidden', 'true');
  }

  mobileMenuBtn && mobileMenuBtn.addEventListener('click', () => mobileNav && mobileNav.classList.contains('open') ? closeMobileNav() : openMobileNav());
  mobileNavClose && mobileNavClose.addEventListener('click', closeMobileNav);
  mobileNav && $$('a', mobileNav).forEach(a => a.addEventListener('click', closeMobileNav));

  // ============================================================
  // CART DRAWER
  // ============================================================
  const cartOverlay   = $('.cart-overlay');
  const cartDrawer    = $('.cart-drawer');
  const cartCloseBtn  = $('.cart-close-btn');

  function openCart() {
    closeMobileNav();
    cartOverlay && cartOverlay.classList.add('open');
    cartOverlay && cartOverlay.setAttribute('aria-hidden', 'false');
    cartDrawer  && cartDrawer.classList.add('open');
    cartDrawer  && cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-open');
    refreshCartDrawer();
  }

  function closeCart() {
    cartOverlay && cartOverlay.classList.remove('open');
    cartOverlay && cartOverlay.setAttribute('aria-hidden', 'true');
    cartDrawer  && cartDrawer.classList.remove('open');
    cartDrawer  && cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-open');
  }

  $$('.header-cart-btn, [data-open-cart]').forEach(btn =>
    btn.addEventListener('click', openCart)
  );
  cartCloseBtn   && cartCloseBtn.addEventListener('click', closeCart);
  cartOverlay    && cartOverlay.addEventListener('click', closeCart);

  // ESC closes both
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') { closeMobileNav(); closeCart(); }
  });

  // ============================================================
  // CART DRAWER — LIVE REFRESH
  // ============================================================
  async function refreshCartDrawer() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      updateCartCountUI(cart.item_count);

      const body = $('#cart-drawer-body');
      if (!body) return;

      if (cart.item_count === 0) {
        body.innerHTML = `
          <div class="cart-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1" style="margin:0 auto 1.5rem;display:block">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            <p>Your cart is empty</p>
            <a href="/collections/boudane-watches" class="btn btn-outline" style="font-size:0.65rem">Explore Collection</a>
          </div>`;
        // Remove footer if empty
        const footer = cartDrawer && $('.cart-drawer-footer', cartDrawer);
        if (footer) footer.style.display = 'none';
        return;
      }

      let html = '';
      cart.items.forEach(item => {
        const imgSrc = item.image
          ? item.image.replace(/\.(\w+)$/, '_160x160.$1')
          : '';
        const safeTitle = escapeHtml(item.title || item.product_title || 'Cart item');
        const safeProductTitle = escapeHtml(item.product_title || item.title || 'Cart item');
        const safeKey = encodeURIComponent(item.key);
        html += `
          <div class="cart-item">
            ${imgSrc ? `<img src="${imgSrc}" alt="${safeTitle}" class="cart-item-image" width="86" height="86" loading="lazy">` : ''}
            <div class="cart-item-details">
              <p class="cart-item-name">${safeProductTitle}</p>
              <p class="cart-item-variant">Qty: ${item.quantity}</p>
              <p class="cart-item-price">${formatMoney(item.final_line_price, cart.currency)}</p>
              <a href="/cart/change?id=${safeKey}&quantity=0" class="cart-item-remove">Remove</a>
            </div>
          </div>`;
      });
      body.innerHTML = html;

      // Update footer
      const footer = cartDrawer && $('.cart-drawer-footer', cartDrawer);
      if (footer) {
        footer.style.display = '';
        const priceEl = $('.cart-subtotal-price', footer);
        if (priceEl) priceEl.textContent = `${formatMoney(cart.total_price, cart.currency)}`;
      }
    } catch (e) {
      
    }
  }

  // ============================================================
  // QUANTITY SELECTOR
  // ============================================================
  function bindQtyBtns(ctx = document) {
    $$('.qty-btn', ctx).forEach(btn => {
      // Prevent duplicate listeners
      if (btn._qtyBound) return;
      btn._qtyBound = true;
      btn.addEventListener('click', () => {
        const wrap = btn.closest('.quantity-selector');
        const display = wrap && wrap.querySelector('.qty-input');
        const hidden  = wrap && wrap.closest('form') && wrap.closest('form').querySelector('input[name="quantity"]');
        let val = parseInt((display || hidden || {}).value) || 1;
        if (btn.dataset.action === 'minus') val = Math.max(1, val - 1);
        if (btn.dataset.action === 'plus')  val = Math.min(99, val + 1);
        if (display) display.value = val;
        // BUG FIX: also update the name="quantity" hidden input so form submit works
        if (hidden && hidden !== display) hidden.value = val;
      });
    });
  }
  bindQtyBtns();

  // ============================================================
  // ADD TO CART (AJAX)
  // ============================================================
  $$('.product-add-to-cart-form').forEach(form => {
    let submitting = false;
    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (submitting) return;
      submitting = true;

      const btn = form.querySelector('[type="submit"]');
      const originalText = btn ? btn.textContent : '';
      if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

      const variantId = form.querySelector('input[name="id"]');
      const quantityEl = form.querySelector('input[name="quantity"], .qty-input');
      const quantity = parseInt((quantityEl || {}).value) || 1;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId && variantId.value, quantity })
        });

        if (res.ok) {
          if (btn) btn.textContent = 'Added ✓';
          showToast('Added to your cart');
          updateCartCount();
          setTimeout(openCart, 420);
        } else {
          const data = await res.json().catch(() => ({}));
          if (btn) btn.textContent = data.description || 'Error — Try Again';
          showToast(data.description || 'Could not add to cart');
        }
      } catch {
        if (btn) btn.textContent = 'Error — Try Again';
      }

      setTimeout(() => {
        if (btn) { btn.textContent = originalText; btn.disabled = false; }
        submitting = false;
      }, 2800);
    });
  });

  // ============================================================
  // CART COUNT UPDATE
  // ============================================================
  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      updateCartCountUI(cart.item_count);
    } catch {}
  }

  function updateCartCountUI(count) {
    $$('.cart-count').forEach(el => {
      el.textContent = count;
      // BUG FIX: was using style.display = 'none' inline on page load, now use visibility approach
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  updateCartCount();

  // ============================================================
  // NEWSLETTER FORM
  // ============================================================
  $$('#newsletter-form').forEach(form => {
    let submitting = false;
    form.addEventListener('submit', e => {
      if (submitting) {
        e.preventDefault();
        return;
      }
      const input = form.querySelector('.newsletter-input');
      if (!input || !input.value.trim()) return;
      submitting = true;
      const btn = form.querySelector('button[type="submit"]');
      if (btn) { btn.textContent = 'Subscribing…'; btn.disabled = true; }
      // Allow Shopify's customer/newsletter form to submit normally.
    });
  });

  // ============================================================
  // FAQ ACCORDION
  // ============================================================
  $$('.faq-item').forEach(item => {
    const question = item.querySelector('.faq-question');
    if (!question) return;
    question.addEventListener('click', () => {
      const isOpen = item.classList.contains('open');
      $$('.faq-item.open').forEach(i => { if (i !== item) i.classList.remove('open'); });
      item.classList.toggle('open', !isOpen);
    });
  });

  // ============================================================
  // SCROLL ANIMATIONS (IntersectionObserver)
  // ============================================================
  const fadeEls = $$('.fade-up');
  if (fadeEls.length && 'IntersectionObserver' in window) {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    fadeEls.forEach(el => obs.observe(el));
  } else {
    fadeEls.forEach(el => el.classList.add('visible'));
  }

  // ============================================================
  // ACTIVE NAV LINK
  // ============================================================
  $$('.header-nav a, .footer-col a').forEach(link => {
    const href = link.getAttribute('href');
    const path = window.location.pathname;
    if (!href) return;
    const isWatchRoute = (path.startsWith('/products/') || path.startsWith('/collections/')) && href.includes('/collections/boudane-watches');
    if ((href === '/' ? path === '/' : path.startsWith(href)) || isWatchRoute) {
      link.classList.add('active');
    }
  });

  // ============================================================
  // PRODUCT GALLERY — thumbnail switching
  // (Also defined inline in product.liquid for safety — this is the global handler)
  // ============================================================
  $$('.gallery-thumb').forEach(thumb => {
    if (thumb._galBound) return;
    thumb._galBound = true;
    thumb.addEventListener('click', () => {
      const mainImg = $('#gallery-main-img');
      if (mainImg) {
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src    = thumb.dataset.src    || mainImg.src;
          mainImg.srcset = thumb.dataset.srcset || '';
          mainImg.style.opacity = '1';
        }, 220);
      }
      $$('.gallery-thumb').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });

  // ============================================================
  // TOAST NOTIFICATION
  // ============================================================
  let toastTimeout;
  function showToast(message) {
    let toast = $('.success-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'success-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    clearTimeout(toastTimeout);
    toast.classList.remove('show');
    // Force reflow for re-animation
    void toast.offsetWidth;
    toast.classList.add('show');
    toastTimeout = setTimeout(() => toast.classList.remove('show'), 3200);
  }

  // ============================================================
  // PAGE ENTER ANIMATION
  // ============================================================
  document.body.classList.add('page-enter');

  // ============================================================
  // CART PAGE — updateQty helper (called from inline onclick in cart.liquid)
  // ============================================================
  window.updateQty = function (key, qty) {
    if (qty < 0) qty = 0;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(() => window.location.reload());
  };

})();
